import { useState } from 'react';
import { resendCode, verifyCode as verifyCodeService } from 'services/signup';
import {
  setForgotPassword as forgotPasswordService,
  setConfirmPassword as confirmPasswordService,
  verifyUser,
} from 'services/user';
import { RouteComponentProps } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Footer, Header, Input } from 'components';
import { SmallBtn, EmptyBtn } from 'components/Buttons';

import { VerifyCodeContainer, FormContainer, CodeInput, MainButton } from './style';

import LogoRegenesis from 'assets/logo/logo-regenesis-final.svg';
import Seta from 'assets/icon/seta.svg';
import smoothscroll from 'smoothscroll-polyfill';

smoothscroll.polyfill();

interface props extends RouteComponentProps {}

interface IState {
  state: {
    email: string;
    type: string;
    unverified?: boolean;
  };
}

function ResetPassword({ history, location }: props) {
  const [showPasswordInput, setShowPasswordInput] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [verifyCode, setVerifyCode] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [repeatPassword, setRepeatPassword] = useState<string>('');
  const [errorPassword, setErrorPassword] = useState<string>('');
  const [errorRepeatPassword, setErrorRepeatPassword] = useState<string>('');
  const { state } = location as IState;

  const type = state?.type || 'login';

  function handleArrow() {
    history.push('/login');
  }

  const handleChangeInputCode = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ): void => {
    if (event.target.value === '') {
      const removeChar = verifyCode.slice(0, index);

      setVerifyCode(removeChar);
      return;
    }

    setVerifyCode(verifyCode + event.target.value);
    document.getElementById(`input-verify-code-number-${index + 1}`)?.focus();

    if (index === 5) {
      document.getElementById(`input-verify-code-number-${index}`)?.blur();
    }
  };

  const handlePasswordReset = async (): Promise<void> => {
    if (newPassword !== repeatPassword) {
      setErrorPassword('As senhas não se correspondem, informe senhas iguais.');
      setErrorRepeatPassword(
        'As senhas não se correspondem, informe senhas iguais.',
      );
      return;
    }

    if (!verifyCode) {
      toast.error('Preencha o código de verificação');
      return;
    }

    const data = {
      email,
      password: newPassword,
      code: verifyCode,
    };

    await confirmPasswordService(data)
      .then(response => {
        if (response) {
          toast.success('Senha alterada com sucesso');
          history.push('/login');
        }
      })
      .catch(() => {
        toast.error(
          '⚠ Algo deu errado ao salvar sua nova senha. Tente novamente.',
        );
      });
  };

  const handleVerifyCodeSubmit = async (): Promise<void> => {
    if (!verifyCode || !verifyCode.length) {
      toast.error('Preencha o código de verificação');
      return;
    }

    const data = {
      email: state.email || email,
      code: verifyCode,
    };

    const isValid = await verifyCodeService(data);
    if (isValid) {
      toast.success('Código verificado com sucesso');
      history.push('/login');
    } else {
      toast.error('⚠ Erro ao verificar código. Tente novamente');
    }
  };

  const handleNewCode = async (): Promise<void> => {
    const codeResponse = await resendCode(email);
    if (codeResponse) {
      toast.success('Código reenviado com sucesso!');
    }
  };

  const handleVerifyEmail = async (): Promise<void> => {
    const { email_exists } = await verifyUser(email);

    if (!email_exists) {
      toast.error('⚠ E-mail não encontrado.');
    } else {
      await resendCode(email);
      history.push('/redefinir', {
        type: 'codeSecondStep',
      });
    }
  };

  const sendVerifyCodeToEmail = async (): Promise<void> => {
    if (!email || !email.replace(/[!@#$%¨&()_+=~^<>*|"]/g, '').trim()) {
      toast.error('Preencha o campo Email');
      return;
    }

    await forgotPasswordService(email).then(response => {
      if (response) {
        setShowPasswordInput(true);
      }
    });
  };

  return (
    <VerifyCodeContainer>
      <Header reduceHeader={true} />

      <FormContainer>
        <div className="logo-wrapper">
          <img src={LogoRegenesis} alt="regenesis" />
        </div>
        {type === 'login' ? (
        <FormContainer>
          {showPasswordInput ? (
            <>
            <div className="title">
              <h2>Atualize sua senha</h2>
            </div>

              <div className="space-text-2">
                <h4 className='label'>Código</h4>

                <CodeInput>
                  {Array(6)
                    .fill(6)
                    .map((_, index) => (
                      <input
                        key={index}
                        type="number"
                        name=""
                        id={`input-verify-code-number-${index}`}
                        value={verifyCode.charAt(index)}
                        onChange={event => handleChangeInputCode(event, index)}
                      />
                    ))}
                </CodeInput>
              </div>

              <Input
                label={'Nova senha'}
                type="password"
                onChange={event => setNewPassword(event.target.value)}
                value={newPassword}
                id={'new-password-input'}
                alert="A senha precisa conter no mínimo 8 caracteres, incluindo letras maíusculas, minúsculas e números"
              >
                {errorPassword && <span>{errorPassword}</span>}
              </Input>

              <Input
                label={'Confirme a nova senha'}
                type="password"
                onChange={event => setRepeatPassword(event.target.value)}
                value={repeatPassword}
                id={'repeat-password-input'}
              >
                {errorRepeatPassword && <span>{errorRepeatPassword}</span>}
              </Input>

              <div className="container-btn">
                <MainButton onClick={handlePasswordReset}>Enviar</MainButton>
              </div>
            </>
          ) : (
            <>
             <div className="title">
                <h2 className="space-text">
                    Insira seu email para receber o código de verificação
                </h2>
              </div>

              <Input
                label="Email"
                onChange={event => setEmail(event.target.value)}
                type="text"
                value={email}
              />

              <div className="container-btn">
                <MainButton onClick={sendVerifyCodeToEmail}>Enviar</MainButton>
              </div>
            </>
          )}
        </FormContainer>
      ) : (
        <>
          {type === 'codeFirstStep' ? (
            <FormContainer>
              <h4 className="space-text">Insira seu email</h4>
              {state.unverified && (
                <p>
                  Usuário não confirmado, insira seu email para receber o
                  código, confirmar o cadastro e acessar o sistema.
                </p>
              )}
              <Input
                label="Email"
                onChange={event => setEmail(event.target.value)}
                type="text"
                value={email}
              />

              <div className="container-btn">
                <MainButton onClick={handleVerifyEmail}>Enviar</MainButton>
              </div>
            </FormContainer>
          ) : (
            <>
              {type === 'codeSecondStep' ? (
                <FormContainer>
                    <div className="title">
                        <h2>Verifique seu email</h2>
                    </div>
                  <div className="space-text-2">
                    <h5>Código de verificação</h5>
                    <CodeInput>
                      {Array(6)
                        .fill(6)
                        .map((_, index) => (
                          <input
                            key={index}
                            type="number"
                            name=""
                            id={`input-verify-code-number-${index}`}
                            value={verifyCode.charAt(index)}
                            onChange={event =>
                              handleChangeInputCode(event, index)
                            }
                          />
                        ))}
                    </CodeInput>
                  </div>
                  <div className="container-btns">
                    <p onClick={handleNewCode}>Reenviar código</p>
                    <EmptyBtn onClick={() => history.push('/')}>
                      Cancelar
                    </EmptyBtn>
                    <SmallBtn onClick={handleVerifyCodeSubmit}>Enviar</SmallBtn>
                  </div>
                </FormContainer>
              ) : (
                <>
                  <FormContainer>
                    <div className="title">
                        <h2>Para prosseguir, é preciso validar seu e-mail</h2>
                    </div>

                    <h4 className='information'>
                      Um código de confirmação foi enviado para o e-mail: <b>{state.email}</b> <br />
                      Verifique também sua caixa de spam.
                    </h4>
                    
                    <h4 className='label'>Código</h4>

                    <CodeInput>
                      {Array(6)
                        .fill(6)
                        .map((_, index) => (
                          <input
                            key={index}
                            type="number"
                            name=""
                            id={`input-verify-code-number-${index}`}
                            value={verifyCode.charAt(index)}
                            onChange={event =>
                              handleChangeInputCode(event, index)
                            }
                          />
                        ))}
                    </CodeInput>

                    <div className="container-btns">
                      <EmptyBtn onClick={() => history.push('/')}>
                        Cancelar
                      </EmptyBtn>
                      <MainButton onClick={handleVerifyCodeSubmit}>
                        Confirmar <img className="icon" src={Seta} alt="Arrow" />
                      </MainButton>
                    </div>
                  </FormContainer>
                </>
              )}
            </>
          )}
        </>
      )}
      </FormContainer>

      <div className="footer-login">
        <Footer
          backgroundColor={'var(--color-secondary-background-color)'}
          hasLogo
        />
      </div>
    </VerifyCodeContainer>
  );
}

export default ResetPassword;
